import React from 'react';

import {
  Alert,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  IconButtonProps,
  Stack,
} from '@mui/material';
import {ExpandMore as ExpandMoreIcon} from '@mui/icons-material';
import {styled} from '@mui/material/styles';
import {defineMessages, useIntl} from 'react-intl';
import {useMutation, useQueryClient} from 'react-query';
import {useNavigate} from 'react-router';

import API, {Types, useCsrfToken} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {ConfirmationDialog, useErrorHandler} from '@wheeebo/polaris-ui';

const msgs = defineMessages({
  alertDeleteTitle: {
    id: 'pages.member.vehicle.SettingsCard.alert.deleteTitle',
    defaultMessage: 'Delete the vehicle',
  },
  alertDeleteContent: {
    id: 'pages.member.vehicle.SettingsCard.alert.deleteContent',
    defaultMessage: 'Pressing the delete button will show you a dialog to confirm your intent before proceeding.',
  },
  alertDeactivateTitle: {
    id: 'pages.member.vehicle.SettingsCard.alert.invalidateActivationTitle',
    defaultMessage: 'Deactivate the vehicle',
  },
  alertDeactivateContent: {
    id: 'pages.member.vehicle.SettingsCard.alert.invalidateActivationContent',
    defaultMessage:
      'polaris assigns a unique key to each vehicle, which cryptographically ensures telemetry is truely sent ' +
      'from the vehicle without unauthorized interception and alteration. The key can be revoked and regenerated at ' +
      'any time when the owner would like to do so by pressing the "Deactivate" button.',
  },
  headerTitle: {
    id: 'pages.member.vehicle.SettingsCard.header.title',
    defaultMessage: 'Settings',
  },
  headerSubTitle: {
    id: 'pages.member.vehicle.SettingsCard.header.subTitle',
    defaultMessage: 'Critical action. Be careful when you execute this.',
  },
  buttonDelete: {
    id: 'pages.member.vehicle.SettingsCard.button.delete',
    defaultMessage: 'Delete',
  },
  buttonDeactivate: {
    id: 'pages.member.vehicle.SettingsCard.button.deactivate',
    defaultMessage: 'Deactivate',
  },
  dialogDeleteConfirmationTitle: {
    id: 'pages.member.vehicle.SettingsCard.dialog.deleteConfirmationTitle',
    defaultMessage: 'Confirm',
  },
  dialogDeleteConfirmationContent: {
    id: 'pages.member.vehicle.SettingsCard.dialog.deleteConfirmationContent',
    defaultMessage: 'Are you really going to delete this vehicle?',
  },
  dialogDeactivateConfirmationTitle: {
    id: 'pages.member.vehicle.SettingsCard.dialog.deactivateConfirmationTitle',
    defaultMessage: 'Confirm',
  },
  dialogDeactivateConfirmationContent: {
    id: 'pages.member.vehicle.SettingsCard.dialog.deactivateConfirmationContent',
    defaultMessage: 'Are you really going to deactivate this vehicle?',
  },
  successVehicleDeleted: {
    id: 'pages.member.vehicle.SettingsCard.success.vehicleDeleted',
    defaultMessage: 'Vehicle deleted',
  },
  successVehicleDeactivated: {
    id: 'pages.member.vehicle.SettingsCard.success.vehicleDeactivated',
    defaultMessage: 'Vehicle deactivated',
  },
});

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ExpandMore = styled(({expand, ...props}: ExpandMoreProps) => {
  return <IconButton {...props} />;
})(({theme, expand}) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

interface Props {
  vehicleId: Types.UUID;
}

function SettingsCard({vehicleId}: Props): JSX.Element {
  const intl = useIntl();
  const {csrfToken} = useCsrfToken();
  const navigate = useNavigate();
  const {showAndEmitJsErrorEvent} = useErrorHandler();
  const {showSuccess} = useSnackbar();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const {mutate: deleteVehicle} = useMutation<unknown, unknown>(() => API.Vehicles.delete_(csrfToken, vehicleId), {
    onSuccess: async () => {
      navigate('/vehicles');
      showSuccess(intl.formatMessage(msgs.successVehicleDeleted));
      // Clear the cache after the navigation is completed. Otherwise, the data for the vehicle just deleted will be
      // requested, which always results in a failure.
      setTimeout(() => queryClient.invalidateQueries(API.Vehicles.CacheKey), 500);
    },
    onError: showAndEmitJsErrorEvent,
  });
  const {mutate: deactivateVehicle} = useMutation<unknown, unknown>(
    () => API.Vehicles.deactivate(csrfToken, vehicleId),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(API.Vehicles.CacheKey);
        showSuccess(intl.formatMessage(msgs.successVehicleDeactivated));
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  return (
    <Card>
      <CardHeader
        title={intl.formatMessage(msgs.headerTitle)}
        subheader={intl.formatMessage(msgs.headerSubTitle)}
        action={
          <ExpandMore
            expand={expanded}
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            aria-label="show more">
            <ExpandMoreIcon />
          </ExpandMore>
        }
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          <Stack gap={1}>
            <Alert
              icon={false}
              severity="error"
              sx={{width: '100%'}}
              action={
                <Button color="error" onClick={() => setDeactivateDialogOpen(true)} sx={{alignSelf: 'center'}}>
                  {intl.formatMessage(msgs.buttonDeactivate)}
                </Button>
              }>
              <AlertTitle>{intl.formatMessage(msgs.alertDeactivateTitle)}</AlertTitle>
              {intl.formatMessage(msgs.alertDeactivateContent)}
            </Alert>
            <Alert
              icon={false}
              severity="error"
              sx={{width: '100%'}}
              action={
                <Button color="error" onClick={() => setDeleteDialogOpen(true)} sx={{alignSelf: 'center'}}>
                  {intl.formatMessage(msgs.buttonDelete)}
                </Button>
              }>
              <AlertTitle>{intl.formatMessage(msgs.alertDeleteTitle)}</AlertTitle>
              {intl.formatMessage(msgs.alertDeleteContent)}
            </Alert>
          </Stack>
        </CardContent>
      </Collapse>
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={intl.formatMessage(msgs.dialogDeleteConfirmationTitle)}
        description={intl.formatMessage(msgs.dialogDeleteConfirmationContent)}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          setDeleteDialogOpen(false);
          deleteVehicle();
        }}
      />
      <ConfirmationDialog
        open={deactivateDialogOpen}
        title={intl.formatMessage(msgs.dialogDeactivateConfirmationTitle)}
        description={intl.formatMessage(msgs.dialogDeactivateConfirmationContent)}
        onCancel={() => setDeactivateDialogOpen(false)}
        onConfirm={() => {
          setDeactivateDialogOpen(false);
          deactivateVehicle();
        }}
      />
    </Card>
  );
}
export default React.memo(SettingsCard);
